/**
 * Mobile footer leaderboard ad
 *
 * Registered in assets/js/rspack.common.js as `mobileFooterAd`.
 *
 * The slot itself is defined in templates/dfp/mobile_footer_ad.html.
 * templates/dfp/head.html owns page-level pubads config and
 * enableServices.
 *
 */

const SLOT_ID = 'dfp-mobilefooter-mobile';
const CONTAINER_ID = 'mobile-footer-ad';

// Fraction of the first in-content ad that must be visible before the
// footer is dismissed.
const VISIBILITY_THRESHOLD = 0.25;

// On short articles the first in-content ad can already be on screen at
// load, which would dismiss the footer before the reader does anything.
// When true, the observer is only armed after the first scroll.
const REQUIRE_SCROLL = true;

const HOUSE_AD = null;

let container = null;
let dismissed = false;
let observer = null;
let armed = false;

const filledSlots = new Set();

function reveal() {
  if (!container || dismissed) return;
  container.classList.add('is-ready');
  document.documentElement.style.setProperty(
    '--mobile-footer-ad-height',
    `${container.offsetHeight}px`,
  );
}

function dismiss() {
  if (dismissed) return;
  dismissed = true;

  if (observer) {
    observer.disconnect();
    observer = null;
  }

  if (!container) return;

  container.classList.add('is-dismissed');
  document.documentElement.style.setProperty('--mobile-footer-ad-height', '0px');

  const finish = () => {
    container.classList.add('is-gone');
    container.removeEventListener('transitionend', finish);
  };
  container.addEventListener('transitionend', finish);
  // Backstop for reduced motion or a backgrounded tab, where the
  // transition never fires.
  window.setTimeout(finish, 400);
}

function renderHouseAd() {
  const target = document.getElementById(SLOT_ID);
  if (!HOUSE_AD || !target) return;

  const link = document.createElement('a');
  link.href = HOUSE_AD.href;
  link.className = 'mobile-footer-ad__house';

  const img = document.createElement('img');
  img.src = HOUSE_AD.img;
  img.alt = HOUSE_AD.alt;
  img.width = 320;
  img.height = 50;

  link.appendChild(img);
  target.innerHTML = '';
  target.appendChild(link);
  reveal();
}

/**
 * Watch the first filled in-content ad and dismiss the footer once the
 * reader scrolls it into view.
 */

let observedTarget = null;

function watchFirstInContentAd() {
  if (dismissed) return;

  const target = [...document.querySelectorAll('[id^="dfp-"]')]
    .find((el) => el.id !== SLOT_ID && filledSlots.has(el.id));

  if (!target || target === observedTarget) return;

  if (observer) observer.disconnect();

  observedTarget = target;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) dismiss();
    },
    { threshold: VISIBILITY_THRESHOLD },
  );

  observer.observe(target);
}

function arm() {
  if (armed || dismissed) return;
  armed = true;
  watchFirstInContentAd();
}

function init() {
  if (window.dfp_visibility !== 'mobile') return;

  container = document.getElementById(CONTAINER_ID);
  if (!container) return;

  if (REQUIRE_SCROLL) {
    window.addEventListener('scroll', arm, { passive: true, once: true });
  }

  window.googletag = window.googletag || { cmd: [] };
  window.googletag.cmd.push(() => {
    window.googletag.pubads().addEventListener('slotRenderEnded', (event) => {
      const id = event.slot.getSlotElementId();
      if (!event.isEmpty) filledSlots.add(id);

      // Slot may fill after the reader has already scrolled, retry
      // whenever new inventory lands and hasn't latched on yet
      if (armed && !dismissed) watchFirstInContentAd();

      if (id !== SLOT_ID) return;

      if (event.isEmpty) {
        renderHouseAd();
      } else {
        reveal();
      }

      // Deferred so slots rendering in the same batch have registered
      // before we pick the first one.
      if (!REQUIRE_SCROLL) window.setTimeout(arm, 0);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
